import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Send, Facebook, Linkedin, Instagram, ChevronRight, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    role: '',
    message: ''
  });
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsNewsletterSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('email', newsletterEmail);
      
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        toast.success(data.message || 'Successfully subscribed to the newsletter!');
        setTimeout(() => setNewsletterSuccess(false), 3000);
      } else {
        toast.error(data.detail || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsContactSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', contactForm.name);
      formData.append('email', contactForm.email);
      formData.append('subject', contactForm.role || 'General Inquiry');
      formData.append('message', contactForm.message);
      
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', role: '', message: '' });
        toast.success(data.message || 'Message sent successfully!');
        setTimeout(() => setContactSuccess(false), 3000);
      } else {
        toast.error(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsContactSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-gold-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Contact Us</span>
            </nav>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Get in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-green-600">
                Touch
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              We'd love to hear from you. Whether you're interested in joining our community, have questions, or want to partner with us, reach out today.
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-br from-brand-green to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Join Our Email Newsletter</h2>
            <p className="text-green-100">Stay updated with our latest news, events, and opportunities.</p>
          </div>
          
          <form onSubmit={handleNewsletterSubmit} className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                className="flex-1 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:border-white focus:ring-white"
              />
              <Button 
                type="submit" 
                disabled={isNewsletterSubmitting}
                className="h-12 bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-6 shadow-lg"
              >
                {isNewsletterSubmitting ? (
                  'Subscribing...'
                ) : newsletterSuccess ? (
                  <><Check className="w-5 h-5 mr-2" /> Subscribed!</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> Subscribe</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-navy-900 mb-2">Want to Learn More?</h2>
              <p className="text-navy-600 mb-8">Contact us and let us know your role in Early Childhood Education.</p>
              
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-navy-700 font-medium">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-navy-700 font-medium">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                      className="h-12"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-navy-700 font-medium">Your Role in ECE</Label>
                  <Input
                    id="role"
                    type="text"
                    placeholder="e.g., Teacher, Administrator, Student"
                    value={contactForm.role}
                    onChange={(e) => setContactForm({...contactForm, role: e.target.value})}
                    className="h-12"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-navy-700 font-medium">Message</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us how we can help you..."
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                    className="min-h-32 resize-none"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isContactSubmitting}
                  className="w-full h-12 bg-brand-green hover:bg-green-600 text-white font-semibold shadow-xl shadow-green-500/20"
                >
                  {isContactSubmitting ? (
                    'Sending...'
                  ) : contactSuccess ? (
                    <><Check className="w-5 h-5 mr-2" /> Message Sent!</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Send Message</>
                  )}
                </Button>
              </form>
            </div>
            
            {/* Contact Info */}
            <div className="lg:pl-8">
              <h2 className="text-3xl font-bold text-navy-900 mb-8">Contact Information</h2>
              
              <div className="space-y-6 mb-12">
                <Card className="border-0 shadow-lg shadow-navy-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/20">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-1">Email Us</h4>
                      <a href="mailto:dcmeninece@gmail.com" className="text-brand-green hover:text-green-700 transition-colors">
                        dcmeninece@gmail.com
                      </a>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg shadow-navy-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-gold-500/20">
                      <MapPin className="w-6 h-6 text-navy-900" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-1">Location</h4>
                      <p className="text-navy-600">Washington, D.C.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Social Links */}
              <div>
                <h4 className="font-semibold text-navy-900 mb-4">Follow Us</h4>
                <div className="flex gap-3">
                  <a href="https://www.facebook.com/DCMenInECE" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-navy-100 hover:bg-brand-green flex items-center justify-center transition-colors duration-300 group" aria-label="Facebook">
                    <Facebook className="w-5 h-5 text-navy-600 group-hover:text-white transition-colors" />
                  </a>
                  <a href="https://www.linkedin.com/company/dc-men-in-ece/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-navy-100 hover:bg-brand-green flex items-center justify-center transition-colors duration-300 group" aria-label="LinkedIn">
                    <Linkedin className="w-5 h-5 text-navy-600 group-hover:text-white transition-colors" />
                  </a>
                  <a href="https://www.instagram.com/dcmeninece/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-navy-100 hover:bg-brand-green flex items-center justify-center transition-colors duration-300 group" aria-label="Instagram">
                    <Instagram className="w-5 h-5 text-navy-600 group-hover:text-white transition-colors" />
                  </a>
                </div>
              </div>
              
              {/* Conference CTA */}
              <Card className="mt-12 border-0 shadow-xl bg-gradient-to-br from-navy-900 to-navy-800">
                <CardContent className="p-8">
                  <h4 className="text-xl font-bold text-white mb-3">Attend Our Conference</h4>
                  <p className="text-navy-300 mb-6">We'd love to meet you at our 2026 Annual Conference!</p>
                  <Link to="/conference">
                    <Button className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold shadow-lg">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
